/**
 * El carrito del punto de venta.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  DOS DECISIONES IMPORTANTES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. **El carrito se guarda en el dispositivo.** Si el navegador se recarga, el
 *    celular se bloquea o la app se cierra a mitad de una venta, el carrito
 *    sigue ahí. En un mostrador con cola, perder un carrito de 15 productos es
 *    inaceptable.
 *
 * 2. **Los totales que se calculan aquí son SOLO PARA MOSTRAR.** El cálculo que
 *    vale es el del backend, con `Decimal`. Aquí se replica la misma fórmula
 *    (mismos pasos, mismos redondeos) para que el cajero vea exactamente lo que
 *    se va a cobrar, pero la verdad siempre viene del servidor.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { loQueSeCobra, recargoDe } from "@/features/pos/recargas";
import { exentoPorImpuesto } from "@/lib/exentos";
import type { IngredienteQuitado, ItemListRow, QuitableDeLista } from "@/lib/types";
import { quitablesDelPlato } from "@/lib/funcionesDelRestaurante";

export type LineaCarrito = {
  /** Clave única de la línea: mismo producto con distinta variante son líneas distintas. */
  key: string;
  itemId: string;
  variantId: string | null;
  kind: "product" | "service";

  nombre: string;
  varianteNombre: string | null;

  /** Precio base sin impuesto, como texto tal cual vino del backend. */
  precioBase: string;
  tasaImpuesto: string;
  impuestoIncluido: boolean;
  /** Nombre del impuesto, para poder imprimirlo en un recibo sin conexión. */
  impuestoNombre: string | null;

  /**
   * De qué impuestos de pedido está exento: la harina sin IVA.
   *
   * ⚠️ Viaja con la línea para que el total del cobro dé lo mismo que el del
   * servidor. Sin esto, el cajero vería el IVA sobre la harina, cobraría eso,
   * y el servidor rechazaría la venta por «sobran 4,80». Ver `lib/exentos.ts`.
   *
   * Opcional porque los carritos guardados en el aparato antes de que esto
   * existiera no lo traen, y tienen que seguir abriéndose.
   */
  exentoDe?: string[];

  cantidad: number;
  descuento: number;

  permiteDecimales: boolean;
  /**
   * Cómo se vende: `kg`, `l`, `unit`…
   *
   * Viaja con la línea para que el carrito pueda decir «250 g» en vez de
   * «0,25». La cantidad se guarda en la unidad de venta porque es lo que se
   * cobra, pero «0,25» no es lo que pidió nadie.
   */
  unidad: string | null;
  requierePersonal: boolean;

  /**
   * De cuánto es la recarga. Vacío en todo lo que no sea una.
   *
   * ⚠️ VIAJA HASTA LA VENTA, Y EL PRECIO NO.
   *
   * `precioBase` aquí es solo para que el cajero vea el total mientras arma el
   * carrito; lo que se cobra de verdad lo calcula el servidor a partir de este
   * monto y del recargo del catálogo. Ver `recargas.ts`.
   */
  monto: number | null;

  staffUserId: string | null;
  staffNombre: string | null;
  /** `{lista_id: precio}`. Vacío en casi todo el catálogo. */
  preciosPorLista?: Record<string, string>;

  /**
   * Los extras elegidos: «+ Queso», «+ Tocineta» (2026-10-15).
   *
   * ⚠️ Su precio NO va en `precioBase`: se suma en `precioDeLinea`, así vale
   * igual con cualquier lista. Al cobrar viajan solo sus ids y el servidor
   * pone el precio de cada uno, como el del plato.
   *
   * Opcional: los carritos guardados en el aparato antes de esto no lo traen.
   */
  opciones?: ExtraEnCarrito[];

  /**
   * Lo que el cliente pidió quitar: «sin tomate» (2026-10-27).
   *
   * Entra en la clave de la línea, como los extras: «hamburguesa» y
   * «hamburguesa sin tomate» no se juntan en «× 2». Al cobrar viajan solo los
   * ids y el servidor no descuenta esos ingredientes. El precio no cambia.
   */
  sin?: IngredienteQuitado[];

  /**
   * Lo que se le PUEDE quitar a este plato, copiado del catálogo al agregarlo
   * y ya filtrado por su variación. Viaja con la línea para que el carrito lo
   * ofrezca sin buscar el plato otra vez, también sin internet.
   */
  quitables?: QuitableDeLista[];

  /*
    ⚠️ AQUÍ VIVÍA EL PRECIO DE MAYOR POR CANTIDAD, Y SE RETIRÓ.

    Hacía lo mismo que una lista de precios llamada «Mayor», solo que saltando
    por cantidad en vez de eligiéndose por cliente. El dueño se quedó con las
    listas: «mejor quitar esas funciones y que usen los tipos de precios».

    Lo que hace ese trabajo ahora es `preciosPorLista`, unas líneas más arriba.
  */
};

/** Un extra de una línea: lo justo para enseñarlo y sumarlo. */
export type ExtraEnCarrito = { id: string; nombre: string; precio: string };

/** Lo que suman los extras de una línea, por unidad. */
export function sumaDeExtras(l: Pick<LineaCarrito, "opciones">): number {
  return (l.opciones ?? []).reduce((s, o) => s + (Number.parseFloat(o.precio) || 0), 0);
}

type EstadoCarrito = {
  lineas: LineaCarrito[];
  descuentoGlobal: number;
  clienteId: string | null;
  clienteNombre: string | null;
  nota: string;

  /**
   * Con qué lista de precios se está cobrando: «Mayor», «Efectivo», «Binance».
   *
   * `null` = los precios normales, que es la inmensa mayoría de las ventas.
   *
   * ⚠️ Se guarda en el carrito y NO en un ajuste del negocio, a propósito: es
   * de ESTA venta. Si quedara pegado, el cajero le haría precio de mayor al
   * siguiente cliente sin darse cuenta — y eso no da ningún error, solo cobra
   * de menos durante toda la tarde.
   */
  listaPreciosId: string | null;
  listaPreciosNombre: string | null;

  /**
   * Qué impuestos de PEDIDO encendio el cajero en esta venta (el IGTF).
   *
   * Van los identificadores y nada mas: el monto lo calcula el servidor sobre
   * lo que se pago en las monedas de cada impuesto. Aqui solo se recuerda cual
   * esta pulsado.
   *
   * ⚠️ Se limpia al terminar la venta, por la misma razon que la lista de
   * precios: si quedara pegado, el cajero le cobraria IGTF al siguiente cliente
   * sin darse cuenta. Y eso no da ningun error — solo cobra de mas toda la
   * tarde.
   */
  impuestosPedido: string[];

  /**
   * De qué cotización salió lo que hay en el carrito.
   *
   * ⚠️ NO CAMBIA NI UN PRECIO. Los precios se vuelven a calcular con el
   * catálogo de hoy, como en cualquier venta. Esto solo viaja hasta el cobro
   * para dejar apuntado que aquel presupuesto acabó cerrándose — que es de
   * donde sale la tasa de cierre, el único número por el que existe la pantalla
   * de cotizaciones.
   *
   * ⚠️ Y se limpia al vaciar, como la lista de precios y los impuestos. Si
   * quedara pegado, la siguiente venta del mostrador marcaría como convertida
   * una cotización que no tiene nada que ver — y eso no da ningún error, solo
   * infla el porcentaje sobre el que el dueño toma decisiones.
   */
  cotizacionId: string | null;
  cotizacionNumero: string | null;

  /**
   * La venta que se está CORRIGIENDO, si se está corrigiendo una.
   *
   * Con esto puesto, cobrar no crea una venta nueva: corrige esa — la original
   * se anula y queda la buena, enlazadas. Ver `corregir_venta` en el servidor.
   *
   * ⚠️ Se limpia al vaciar, igual que la cotización: pegado, la siguiente
   * venta del mostrador anularía una venta que no tiene nada que ver.
   */
  corrigiendo: { id: string; numero: string } | null;
  /** Carga una venta ya hecha para corregirla. REEMPLAZA lo que hubiera. */
  cargarParaCorregir: (venta: import("@/lib/types").Sale) => void;

  /**
   * El apartado que se está EDITANDO, si se está editando uno (2026-10-26).
   *
   * Con esto puesto, «Cobrar» no cobra: guarda el apartado con lo que haya en
   * el carrito. Lo ya pagado (`pagado`) pasa entero y el resto queda como
   * saldo. Ver `editar` en `credit/apartados.py`.
   *
   * ⚠️ Se limpia al vaciar, igual que `corrigiendo`.
   */
  editandoApartado: { id: string; numero: string; pagado: string } | null;
  /** Carga un apartado para agregarle o quitarle algo. REEMPLAZA lo que hubiera. */
  cargarParaEditarApartado: (venta: import("@/lib/types").Sale, pagado: string) => void;

  /**
   * Mete un producto al carrito.
   *
   * `cantidad` solo la manda quien ya la sabe: el selector de peso, cuando el
   * cajero escribió «400 g» o «2 dólares de queso». Sin ella entra uno, que es
   * lo correcto para todo lo que se vende por piezas.
   */
  agregar: (
    item: ItemListRow,
    variantId?: string | null,
    varianteNombre?: string | null,
    cantidad?: number,
    /**
     * De cuánto es la recarga, cuando el servicio es de monto variable.
     *
     * Lo manda la franja del punto de venta, que es la única que lo sabe.
     */
    monto?: number,
    /** Los extras elegidos para esta línea. */
    opciones?: ExtraEnCarrito[],
    /** Lo que el cliente pidió quitar: «sin tomate». */
    sin?: IngredienteQuitado[],
  ) => void;
  /**
   * Cambia lo quitado de `cuantas` unidades de una línea («1 de las 3 sin
   * tomate»). Si no son todas, la línea se separa en dos; si ya hay otra
   * igual, se juntan.
   */
  ponerSin: (key: string, sin: IngredienteQuitado[], cuantas: number) => void;
  cambiarCantidad: (key: string, cantidad: number) => void;
  sumarUno: (key: string) => void;
  restarUno: (key: string) => void;
  quitar: (key: string) => void;
  /**
   * Alterna el precio de mayor en una línea.
   *
   * ⚠️ Lo pulsa el cajero. Softfi avisa cuando aplica, pero no lo pone solo:
   * si el precio bajara por su cuenta, el dueño vería el margen caer sin que
   * nadie lo hubiera decidido.
   */
  ponerDescuentoLinea: (key: string, descuento: number) => void;
  ponerPersonal: (key: string, userId: string | null, nombre: string | null) => void;
  ponerDescuentoGlobal: (valor: number) => void;
  ponerCliente: (id: string | null, nombre: string | null) => void;
  ponerListaDePrecios: (id: string | null, nombre: string | null) => void;
  ponerNota: (texto: string) => void;
  alternarImpuestoDePedido: (id: string) => void;
  /** Pone los impuestos de pedido de una vez: al retomar una cuenta en espera. */
  ponerImpuestosDePedido: (ids: string[]) => void;
  /**
   * Pone al día de qué está exenta cada línea, con el catálogo recién bajado.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ POR QUÉ HACE FALTA, SI LA LÍNEA YA LO TRAE AL AGREGARLA
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * La línea copia la exención en el momento de agregarla, y la saca de la
   * copia del catálogo guardada en el aparato. Esa copia se pinta al instante
   * y se refresca unos segundos después. Se vio probándolo: el dueño marca la
   * arepa exenta, el cajero entra a Vender y la agrega antes de que llegue lo
   * fresco — y la línea se queda sin exención para siempre. El cajero cobra
   * el IVA sobre la arepa, el servidor no, y la venta no cuadra.
   *
   * Con esto, en cuanto llega el catálogo fresco las líneas se corrigen solas
   * y el total también. Solo toca las que cambiaron.
   */
  refrescarExentos: (porItem: Map<string, string[]>) => void;
  /**
   * Pone las líneas de una CORRECCIÓN con los datos de hoy del catálogo.
   *
   * ═════════════════════════════════════════════════════════════════════════
   *  ⚠️ LA CAJA ENSEÑABA UN PRECIO Y EL SERVIDOR COBRABA OTRO
   * ═════════════════════════════════════════════════════════════════════════
   *
   * `cargarParaCorregir` arma las líneas con lo que dice la venta original,
   * y el servidor, al cobrar la corrección, vuelve a poner los precios del
   * catálogo de HOY — como en cualquier venta. Si el precio cambió desde la
   * mañana, el cajero cobraba el viejo y el servidor contestaba «el pago no
   * cubre el total».
   *
   * Con esto la Caja enseña lo mismo que se va a cobrar. Y las líneas quedan
   * como si se hubieran tocado en la grilla (sin la variante por defecto en
   * la clave), así que volver a pasar el mismo producto SUMA en vez de abrir
   * otra línea.
   *
   * No toca las recargas (su precio sale del monto) ni lo que ya no está en
   * el catálogo. En un producto con tallas se queda el precio de la venta:
   * el de cada talla no viaja en la lista de la Caja.
   */
  alinearConElCatalogo: (porItem: Map<string, ItemListRow>) => void;
  /**
   * Carga una cotización entera en la caja: líneas, cliente, lista e impuestos.
   *
   * ⚠️ REEMPLAZA lo que hubiera, no lo suma. Quien pulsa «Pasar a venta» va a
   * cobrar ESE presupuesto; mezclarlo con lo que quedara de antes le haría
   * cobrar de más sin que nada avise.
   */
  cargarDesdeCotizacion: (cot: CotizacionEnLaCaja) => void;
  vaciar: () => void;
};

/** Lo que hace falta de una cotización para poder cobrarla. */
export type CotizacionEnLaCaja = {
  id: string;
  numero: string;
  clienteId: string | null;
  clienteNombre: string | null;
  listaPreciosId: string | null;
  impuestos: string[];
  lineas: {
    itemId: string | null;
    variantId: string | null;
    nombre: string;
    varianteNombre: string | null;
    cantidad: number;
    precioBase: string;
    descuento: number;
    kind: "product" | "service";
    tasaImpuesto: string;
    impuestoNombre: string | null;
    exentoDe?: string[];
    /*
      Lo de HOY, cuando se encontró el producto en el catálogo (ver
      `aLaCajaConPreciosDeHoy`). Si faltan, la línea se queda con lo que dice
      el papel de la cotización.
    */
    preciosPorLista?: Record<string, string>;
    impuestoIncluido?: boolean;
    unidad?: string | null;
    requierePersonal?: boolean;
    /** Los extras que pidió el cliente (autoservicio). */
    opciones?: ExtraEnCarrito[];
    /**
     * El monto de un servicio de monto variable: el delivery de un pedido de
     * la página (2026-11-02). Viaja hasta la venta, como el de una recarga.
     */
    monto?: number | null;
  }[];
};

/**
 * La clave de una línea: producto, variante y —en las recargas— el monto. Ver
 * por qué el monto entra en la clave en `agregar`.
 *
 * ⚠️ Quien necesite encontrar la línea que acaba de meter la calcula con esto y
 * no a mano. Retomar una cuenta en espera la armaba a mano, sin el monto, y en
 * las recargas no encontraba su propia línea.
 */
export function claveDeLinea(
  itemId: string,
  variantId: string | null,
  monto?: number | null,
  opciones?: readonly ExtraEnCarrito[] | null,
  sin?: readonly IngredienteQuitado[] | null,
): string {
  const deRecarga = typeof monto === "number" && monto > 0;
  // Con extras distintos son platos distintos: la arepa con queso y la sola
  // no se juntan en «× 2».
  const extras = opciones?.length ? `:x${opciones.map((o) => o.id).sort().join(",")}` : "";
  // Lo mismo con lo quitado: la «sin tomate» no se junta con la completa.
  const quitado = sin?.length ? `:s${sin.map((q) => q.variant_id).sort().join(",")}` : "";
  return `${itemId}:${variantId ?? "default"}${deRecarga ? `:m${monto}` : ""}${extras}${quitado}`;
}

/**
 * Las líneas de una venta ya hecha, tal como se cobraron, para corregirla o
 * para editar un apartado. El servidor vuelve a calcular todo al guardar,
 * igual que en cualquier venta; esto es para que el cajero vea lo que hubo y
 * lo arregle.
 */
function lineasDeUnaVenta(venta: import("@/lib/types").Sale) {
  return {
    lineas: venta.items.map((l) => ({
      key: `${l.item_id}:${l.variant_id ?? "default"}:${l.id}`,
      itemId: l.item_id,
      variantId: l.variant_id,
      kind: l.kind,
      nombre: l.name,
      varianteNombre: l.variant_name,
      precioBase: l.unit_price,
      tasaImpuesto: l.tax_rate,
      impuestoIncluido: l.tax_inclusive,
      impuestoNombre: l.tax_name,
      exentoDe: l.exento_de ?? [],
      cantidad: Number(l.qty),
      descuento: Number(l.discount) || 0,
      permiteDecimales: true,
      unidad: null,
      requierePersonal: Boolean(l.staff_user_id),
      monto: l.monto_base != null ? Number(l.monto_base) : null,
      staffUserId: l.staff_user_id,
      staffNombre: l.staff_name,
    })),
    descuentoGlobal: 0,
    clienteId: venta.customer_id,
    clienteNombre: venta.customer_name,
    nota: venta.note ?? "",
    // ⚠️ La lista con la que se cobró. Sin ella, una venta al mayor se
    // corregía a precio normal y el pago no cuadraba.
    listaPreciosId: venta.lista_precios_id ?? null,
    listaPreciosNombre: venta.lista_precios_nombre ?? null,
    impuestosPedido: (venta.impuestos_de_pedido ?? [])
      .map((t) => t.tax_id)
      .filter((id): id is string => Boolean(id)),
    cotizacionId: null,
    cotizacionNumero: null,
  };
}

export const useCarrito = create<EstadoCarrito>()(
  persist(
    (set, get) => ({
      lineas: [],
      descuentoGlobal: 0,
      clienteId: null,
      clienteNombre: null,
      nota: "",
      listaPreciosId: null,
      listaPreciosNombre: null,
      impuestosPedido: [],
      cotizacionId: null,
      cotizacionNumero: null,
      corrigiendo: null,
      editandoApartado: null,

      agregar: (item, variantId = null, varianteNombre = null, cantidad, monto, opciones, sin) => {
        /*
          ⚠️ EL MONTO ENTRA EN LA CLAVE DE LA LÍNEA.

          Una recarga de 500 y una de 1.000 son dos cosas distintas aunque
          sean el mismo servicio: cuestan distinto y descuentan distinto del
          saldo. Sin el monto en la clave, la segunda se sumaría a la primera
          y quedarían «dos recargas de 500» donde una era de mil.

          Dos de 500 sí se juntan en «× 2», que es lo correcto y lo que el
          servidor cobra: el monto es de CADA recarga.
        */
        const deRecarga = typeof monto === "number" && monto > 0;
        const key = claveDeLinea(item.id, variantId, monto, opciones, sin);
        const existente = get().lineas.find((l) => l.key === key);
        const cuanto = cantidad && cantidad > 0 ? cantidad : 1;

        /*
          Si ya está en el carrito, se SUMA en vez de duplicar la línea: es lo
          que espera el cajero al escanear dos veces el mismo producto.

          ⚠️ Y suma `cuanto`, no uno. Al peso importa: quien pide 300 gramos de
          queso y luego se arrepiente y pide 200 más tiene que acabar con 500,
          no con 300 y un kilo. Antes esto sumaba uno fijo, y ahora que se puede
          entrar con cantidad habría sido un fallo silencioso — la línea diría
          1,3 kg y nadie sabría de dónde salió.
        */
        if (existente) {
          set({
            lineas: get().lineas.map((l) =>
              l.key === key ? { ...l, cantidad: l.cantidad + cuanto } : l,
            ),
          });
          return;
        }

        /*
          ⚠️ UNA VARIACIÓN TIENE SU PRECIO (2026-10-15): la hamburguesa de
          carne vale 6 aunque el plato diga «desde 5». El servidor cobra el
          precio del plato más la diferencia de la variación, en la lista que
          sea; aquí se hace la misma cuenta para que el carrito no diga otro
          número.
        */
        const variacion = variantId
          ? item.variaciones?.find((v) => v.id === variantId)
          : undefined;
        const quitables = quitablesDelPlato(item, variantId);
        const diferencia = variacion
          ? Number(variacion.precio) - Number(item.base_price)
          : 0;
        const conDiferencia = (precio: string) =>
          diferencia ? String(Number(precio) + diferencia) : precio;

        set({
          lineas: [
            ...get().lineas,
            {
              key,
              itemId: item.id,
              variantId,
              kind: item.kind,
              nombre: item.name,
              varianteNombre,
              /*
                En una recarga, el precio de partida sale del monto: 500 con
                50% de recargo son 750. Es la misma cuenta que hace el
                servidor, repetida aquí solo para enseñarla mientras se arma
                el carrito.
              */
              precioBase: deRecarga
                ? String(loQueSeCobra(monto, recargoDe(item)))
                : conDiferencia(item.base_price),
              monto: deRecarga ? monto : null,
              // ⚠️ Los precios de las otras listas viajan CON la línea.
              //
              // Sin esto, el carrito enseñaría el precio normal aunque el
              // cajero eligiera «Mayor», y el recibo saldría con otro número.
              // Un punto de venta que muestra una cifra y cobra otra es un
              // problema esperando a pasar — y aquí sería a favor del cliente,
              // que es la clase de error que nadie reporta.
              // ⚠️ Una recarga no tiene precio de mayor: su precio lo pone el
              // monto. El servidor tampoco mira la lista para estos, así que
              // dejarlos aquí haría que la pantalla enseñara un precio que no
              // se va a cobrar.
              preciosPorLista: deRecarga
                ? {}
                : Object.fromEntries(
                    Object.entries(item.precios ?? {}).map(([lista, p]) => [
                      lista,
                      p == null ? p : conDiferencia(String(p)),
                    ]),
                  ),
              tasaImpuesto: item.tax_rate ?? "0",
              impuestoIncluido: item.tax_inclusive ?? true,
              impuestoNombre: item.tax_name ?? null,
              exentoDe: item.impuestos_exentos ?? [],
              cantidad: cuanto,
              descuento: 0,
              permiteDecimales: Boolean(item.allow_decimal_qty),
              unidad: item.unit ?? null,
              requierePersonal: Boolean(item.requires_staff),
              staffUserId: null,
              staffNombre: null,
              ...(opciones?.length ? { opciones } : {}),
              ...(sin?.length ? { sin } : {}),
              ...(quitables.length ? { quitables } : {}),
            },
          ],
        });
      },

      ponerSin: (key, sin, cuantas) => {
        const antes = get().lineas;
        const i = antes.findIndex((l) => l.key === key);
        const linea = antes[i];
        if (!linea) return;
        const nuevaKey = claveDeLinea(linea.itemId, linea.variantId, linea.monto, linea.opciones, sin);
        if (nuevaKey === key) return;

        // A cuántas se aplica: nunca menos de una ni más de las que hay.
        const n = Math.min(Math.max(1, Math.round(cuantas)), linea.cantidad);
        const quedan = linea.cantidad - n;
        const { sin: _viejo, ...sinNada } = linea;
        const cambiada: LineaCarrito = {
          ...sinNada,
          key: nuevaKey,
          cantidad: n,
          // El descuento es de la línea: se reparte con la cantidad.
          descuento: linea.cantidad > 0 ? (linea.descuento * n) / linea.cantidad : 0,
          ...(sin.length ? { sin } : {}),
        };

        const lineas = antes.flatMap((l): LineaCarrito[] => {
          if (l.key !== key) return [l];
          return quedan > 0
            ? [{ ...l, cantidad: quedan, descuento: l.descuento - cambiada.descuento }]
            : [];
        });
        const igual = lineas.findIndex((l) => l.key === nuevaKey);
        if (igual >= 0) {
          const ya = lineas[igual]!;
          lineas[igual] = {
            ...ya,
            cantidad: ya.cantidad + n,
            descuento: ya.descuento + cambiada.descuento,
          };
        } else {
          // Justo debajo de la que salió, para que el cajero la vea donde tocó.
          lineas.splice(quedan > 0 ? i + 1 : i, 0, cambiada);
        }
        set({ lineas });
      },

      // ⚠️ Al bajar la cantidad por debajo del mínimo, el precio de mayor se
      // apaga solo. Si se quedara pegado, quitar una camisa dejaría once
      // cobradas al precio de doce — y el servidor lo rechazaría al confirmar,
      // con el cliente delante y sin explicación.
      cambiarCantidad: (key, cantidad) =>
        set({
          lineas: get()
            .lineas.map((l) => (l.key === key ? { ...l, cantidad } : l))
            // Poner la cantidad en 0 equivale a quitar la línea.
            .filter((l) => l.cantidad > 0),
        }),

      sumarUno: (key) =>
        set({
          lineas: get().lineas.map((l) =>
            l.key === key ? { ...l, cantidad: l.cantidad + 1 } : l,
          ),
        }),

      restarUno: (key) =>
        set({
          lineas: get()
            .lineas.map((l) =>
              l.key === key
                ? { ...l, cantidad: l.cantidad - 1 }
                : l,
            )
            .filter((l) => l.cantidad > 0),
        }),

      quitar: (key) => set({ lineas: get().lineas.filter((l) => l.key !== key) }),

      ponerDescuentoLinea: (key, descuento) =>
        set({
          lineas: get().lineas.map((l) =>
            l.key === key ? { ...l, descuento: Math.max(0, descuento) } : l,
          ),
        }),

      ponerPersonal: (key, userId, nombre) =>
        set({
          lineas: get().lineas.map((l) =>
            l.key === key ? { ...l, staffUserId: userId, staffNombre: nombre } : l,
          ),
        }),

      ponerDescuentoGlobal: (valor) => set({ descuentoGlobal: Math.max(0, valor) }),
      ponerCliente: (id, nombre) => set({ clienteId: id, clienteNombre: nombre }),
      ponerListaDePrecios: (id, nombre) =>
        set({ listaPreciosId: id, listaPreciosNombre: nombre }),
      ponerNota: (texto) => set({ nota: texto }),

      alternarImpuestoDePedido: (id) =>
        set((estado) => ({
          impuestosPedido: estado.impuestosPedido.includes(id)
            ? estado.impuestosPedido.filter((x) => x !== id)
            : [...estado.impuestosPedido, id],
        })),

      ponerImpuestosDePedido: (ids) => set({ impuestosPedido: [...new Set(ids)] }),

      alinearConElCatalogo: (porItem) =>
        set((estado) => {
          const lineas: LineaCarrito[] = [];
          for (const l of estado.lineas) {
            const item = porItem.get(l.itemId);
            if (!item || l.monto != null) {
              lineas.push(l);
              continue;
            }
            const variantId = item.has_variants ? l.variantId : null;
            const key = claveDeLinea(item.id, variantId, null, l.opciones, l.sin);
            const fresca: LineaCarrito = {
              ...l,
              key,
              variantId,
              precioBase: item.has_variants ? l.precioBase : item.base_price,
              preciosPorLista: item.precios ?? {},
              tasaImpuesto: item.tax_rate ?? "0",
              impuestoIncluido: item.tax_inclusive ?? true,
              impuestoNombre: item.tax_name ?? null,
              exentoDe: item.impuestos_exentos ?? [],
              permiteDecimales: Boolean(item.allow_decimal_qty),
              unidad: item.unit ?? null,
            };
            // Dos líneas del mismo producto en la venta original: una sola.
            const i = lineas.findIndex((x) => x.key === key);
            if (i >= 0) {
              const igual = lineas[i]!;
              lineas[i] = {
                ...igual,
                cantidad: igual.cantidad + fresca.cantidad,
                descuento: igual.descuento + fresca.descuento,
              };
            } else {
              lineas.push(fresca);
            }
          }
          return { lineas };
        }),

      refrescarExentos: (porItem) =>
        set((estado) => {
          let cambio = false;
          const lineas = estado.lineas.map((l) => {
            const frescos = porItem.get(l.itemId);
            if (!frescos) return l;
            const antes = l.exentoDe ?? [];
            const iguales =
              antes.length === frescos.length && antes.every((id) => frescos.includes(id));
            if (iguales) return l;
            cambio = true;
            return { ...l, exentoDe: [...frescos] };
          });
          // Sin cambios no se toca el estado: el carrito se guarda en el
          // aparato en cada cambio, y esto corre cada vez que llega el catálogo.
          return cambio ? { lineas } : estado;
        }),

      cargarParaCorregir: (venta) =>
        set({
          ...lineasDeUnaVenta(venta),
          corrigiendo: { id: venta.id, numero: venta.number },
          editandoApartado: null,
        }),

      cargarParaEditarApartado: (venta, pagado) =>
        set({
          ...lineasDeUnaVenta(venta),
          corrigiendo: null,
          editandoApartado: { id: venta.id, numero: venta.number, pagado },
        }),

      cargarDesdeCotizacion: (cot) =>
        set({
          /*
            ⚠️ Las líneas que perdieron su producto se DESCARTAN.

            `itemId` puede venir vacío: el catálogo se puede limpiar y la
            cotización guarda su copia del nombre y del precio para poder
            reimprimirse igual. Pero para COBRAR hace falta el producto de
            verdad —el servidor descuenta stock y valida precio contra él—, así
            que una línea sin él no se puede vender.

            Se quitan en silencio y el cajero lo ve: el carrito trae menos
            líneas de las que dice el papel. Mandarlas al servidor daría
            «uno de los productos no existe» con el cliente delante y sin decir
            cuál.
          */
          lineas: cot.lineas
            .filter((l) => l.itemId !== null)
            .map((l) => ({
              key: claveDeLinea(l.itemId as string, l.variantId, l.monto ?? null, l.opciones),
              itemId: l.itemId as string,
              variantId: l.variantId,
              kind: l.kind,
              nombre: l.nombre,
              varianteNombre: l.varianteNombre,
              precioBase: l.precioBase,
              tasaImpuesto: l.tasaImpuesto,
              // Los impuestos de PRODUCTO de una cotización se guardan ya
              // calculados aparte; aquí lo que importa es la tasa, que es lo
              // que la caja vuelve a aplicar.
              impuestoIncluido: l.impuestoIncluido ?? false,
              impuestoNombre: l.impuestoNombre,
              exentoDe: l.exentoDe ?? [],
              // Los precios de las listas de HOY, para que la lista de la
              // cotización se aplique igual que la aplica el servidor.
              preciosPorLista: l.preciosPorLista,
              cantidad: l.cantidad,
              descuento: l.descuento,
              // ⚠️ Se permiten decimales: la cotización ya trae la cantidad
              // que se cotizó, y si eran 2,5 kg de algo, negarlo aquí
              // impediría cobrar exactamente lo que dice el papel.
              permiteDecimales: true,
              unidad: l.unidad ?? null,
              requierePersonal: l.requierePersonal ?? false,
              // Una cotización no lleva recargas: son de mostrador, y el monto
              // se pregunta al vender. Solo el delivery de un pedido de la
              // página trae el suyo, el que escribió quien atendió (2026-11-02).
              monto: l.monto ?? null,
              staffUserId: null,
              staffNombre: null,
              ...(l.opciones?.length ? { opciones: l.opciones } : {}),
            })),
          descuentoGlobal: 0,
          clienteId: cot.clienteId,
          clienteNombre: cot.clienteNombre,
          nota: `Cotización ${cot.numero}`,
          listaPreciosId: cot.listaPreciosId,
          listaPreciosNombre: null,
          impuestosPedido: cot.impuestos,
          cotizacionId: cot.id,
          cotizacionNumero: cot.numero,
        }),

      vaciar: () =>
        set({
          lineas: [],
          descuentoGlobal: 0,
          clienteId: null,
          clienteNombre: null,
          nota: "",
          // ⚠️ La lista TAMBIÉN se limpia al terminar la venta. Es la línea que
          // evita el fallo caro: si quedara puesta, el cajero le seguiría
          // haciendo precio de mayor a todos los clientes de la tarde sin que
          // nada avise.
          listaPreciosId: null,
          listaPreciosNombre: null,
          impuestosPedido: [],
          // ⚠️ Por el mismo motivo que la lista: pegada, la siguiente venta
          // marcaría como cerrada una cotización con la que no tiene nada que
          // ver. Y eso no da error, solo infla la tasa de cierre.
          cotizacionId: null,
          cotizacionNumero: null,
          corrigiendo: null,
          editandoApartado: null,
        }),
    }),
    {
      name: "softfi-carrito",
      // Solo persistimos el contenido, no las funciones.
      partialize: (s) => ({
        lineas: s.lineas,
        descuentoGlobal: s.descuentoGlobal,
        clienteId: s.clienteId,
        clienteNombre: s.clienteNombre,
        nota: s.nota,
        listaPreciosId: s.listaPreciosId,
        listaPreciosNombre: s.listaPreciosNombre,
        impuestosPedido: s.impuestosPedido,
        cotizacionId: s.cotizacionId,
        cotizacionNumero: s.cotizacionNumero,
        // ⚠️ Se guarda con las líneas: si se recargara la página sin él, el
        // carrito tendría lo apartado y «Cobrar» lo vendería otra vez.
        editandoApartado: s.editandoApartado,
      }),
    },
  ),
);

// =============================================================================
//  CÁLCULO DE TOTALES (solo para mostrar en pantalla)
// =============================================================================

/** Redondea a 4 decimales, igual que hace el backend en cada paso. */
/**
 * El redondeo del dinero, idéntico al `quantize_storage` del servidor.
 *
 * ⚠️ Se exporta para que la pantalla de cobro use ESTA y no una parecida.
 * Cualquier cuenta de dinero que el navegador tenga que cuadrar con el servidor
 * —el total, el impuesto de pedido— redondea aquí o acaba discutiendo con él
 * por medio céntimo delante del cliente.
 */
export function redondearMonto(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function q(n: number): number {
  return redondearMonto(n);
}

export type TotalesCarrito = {
  subtotal: number;      // Base sin impuesto
  descuentoTotal: number;
  impuestoTotal: number;
  total: number;         // Lo que paga el cliente
  cantidadLineas: number;
  cantidadUnidades: number;
  /** Líneas de servicio que exigen indicar quién atendió y aún no lo tienen. */
  faltaPersonal: LineaCarrito[];

  /**
   * Qué producto lleva cada línea y cuánto suma su base.
   *
   * ⚠️ EXISTE PARA LOS CÓDIGOS ACOTADOS, Y ES LO QUE FALTABA.
   *
   * Un código de «toda la categoría Bebidas» no se puede calcular sin saber qué
   * hay en el carrito. La vista previa mandaba solo el subtotal, así que el
   * servidor no tenía forma de saberlo y rechazaba el código — con el producto
   * delante. Ninguna promoción acotada se pudo aplicar nunca desde el mostrador.
   *
   * Es la MISMA base que se acaba de calcular arriba para el subtotal, no otra
   * cuenta: si fueran dos, el descuento de la vista previa y el del cobro
   * podrían discrepar por un céntimo y nadie sabría cuál creer.
   */
  porLinea: { itemId: string; base: number }[];

  /**
   * Cuánto de la venta está exento de cada impuesto de pedido, y cuánto suman
   * las líneas antes del descuento global. Son lo que necesita el cobro para
   * sacar la harina de la base del IVA. Ver `lib/exentos.ts`.
   *
   * Opcionales porque la mesa del salón arma sus totales a mano; sin ellos,
   * nada está exento — que es como era antes.
   */
  exentoPorImpuesto?: Record<string, number>;
  sumaDeLineas?: number;
};

/**
 * El precio de UNA unidad de la línea, con la lista que se está usando.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LA MISMA REGLA PARA EL TOTAL, PARA CADA LÍNEA Y PARA EL RECIBO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Solo el total la aplicaba. Con «Mayor» elegido, cada línea seguía diciendo
 * el precio normal y el total bajaba: las líneas no sumaban el total, y el
 * recibo impreso sin internet salía igual de mal. Lo encontró la auditoría.
 *
 * El orden es el del servidor: el precio propio de esa lista si el producto lo
 * tiene, y si no, el normal.
 */
export function precioDeLinea(
  l: Pick<LineaCarrito, "precioBase" | "preciosPorLista" | "opciones">,
  listaPreciosId: string | null,
): number {
  const deLaLista = listaPreciosId ? l.preciosPorLista?.[listaPreciosId] : undefined;
  // Los extras se suman encima, en cualquier lista: es lo que hace el servidor.
  return (Number.parseFloat(deLaLista ?? l.precioBase) || 0) + sumaDeExtras(l);
}

/** Si esta línea se está cobrando con el precio de la lista (y no el normal). */
export function usaPrecioDeLista(
  l: Pick<LineaCarrito, "preciosPorLista">,
  listaPreciosId: string | null,
): boolean {
  return Boolean(listaPreciosId && l.preciosPorLista?.[listaPreciosId] != null);
}

export type DesgloseDeLinea = {
  /** El de una unidad, sin impuesto. */
  precio: number;
  /** El de una unidad, como lo paga el cliente. */
  precioConImpuesto: number;
  base: number;
  impuesto: number;
  /** Lo que el cliente paga por la línea entera. */
  total: number;
};

/**
 * Las cuentas de una línea, en el orden del servidor (`split_tax` en
 * `app/core/money.py`): bruto → menos descuento → separar el impuesto.
 */
export function desgloseDeLinea(
  l: Pick<
    LineaCarrito,
    | "precioBase"
    | "preciosPorLista"
    | "tasaImpuesto"
    | "impuestoIncluido"
    | "cantidad"
    | "descuento"
    | "opciones"
  >,
  listaPreciosId: string | null,
): DesgloseDeLinea {
  const precio = precioDeLinea(l, listaPreciosId);
  const tasa = Number.parseFloat(l.tasaImpuesto) || 0;

  const bruto = q(precio * l.cantidad);
  const neto = q(Math.max(0, bruto - l.descuento));

  let base = neto;
  let impuesto = 0;
  if (tasa > 0) {
    if (l.impuestoIncluido) {
      base = q(neto / (1 + tasa));
      impuesto = q(neto - base);
    } else {
      impuesto = q(neto * tasa);
    }
  }

  return {
    precio,
    precioConImpuesto: l.impuestoIncluido || tasa <= 0 ? precio : q(precio * (1 + tasa)),
    base,
    impuesto,
    total: l.impuestoIncluido ? neto : q(neto + impuesto),
  };
}

/**
 * Calcula los totales replicando exactamente los pasos del backend:
 * bruto → menos descuento → separar impuesto → sumar.
 */
export function calcularTotales(
  lineas: LineaCarrito[],
  descuentoGlobal: number,
  /**
   * Con qué lista se está cobrando. `null` = precios normales.
   *
   * ⚠️ Lo que no tenga precio propio en esa lista se calcula con el normal —
   * la misma regla que aplica el servidor. Si aquí se hiciera distinto, el
   * cajero vería un total y el sistema cobraría otro.
   */
  listaPreciosId: string | null = null,
): TotalesCarrito {
  let subtotal = 0;
  let impuestoTotal = 0;
  let descuentoLineas = 0;
  let unidades = 0;
  const porLinea: { itemId: string; base: number }[] = [];
  const paraExentos: { total: number; exentoDe?: string[] }[] = [];

  for (const l of lineas) {
    // ⚠️ MISMO ORDEN QUE EL SERVIDOR, o el cajero ve un total y se cobra otro.
    // Y la MISMA cuenta que pinta cada línea: ver `desgloseDeLinea`.
    const { base, impuesto, total: totalDeLinea } = desgloseDeLinea(l, listaPreciosId);
    paraExentos.push({ total: totalDeLinea, exentoDe: l.exentoDe });

    subtotal = q(subtotal + base);
    porLinea.push({ itemId: l.itemId, base });
    impuestoTotal = q(impuestoTotal + impuesto);
    descuentoLineas = q(descuentoLineas + l.descuento);
    unidades += l.cantidad;
  }

  const total = q(Math.max(0, subtotal + impuestoTotal - descuentoGlobal));

  return {
    subtotal,
    descuentoTotal: q(descuentoLineas + descuentoGlobal),
    impuestoTotal,
    total,
    porLinea,
    exentoPorImpuesto: exentoPorImpuesto(paraExentos),
    sumaDeLineas: q(paraExentos.reduce((n, l) => n + l.total, 0)),
    cantidadLineas: lineas.length,
    cantidadUnidades: unidades,
    faltaPersonal: lineas.filter((l) => l.requierePersonal && !l.staffUserId),
  };
}
